import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import FormFieldForm, { FormFieldValues } from "./FormFieldForm";
import { Tables } from "@/integrations/supabase/types";

type Agent = Tables<"agents">;
type DbFormField = Tables<"form_fields">;

interface FormField extends DbFormField {
  fieldType?: string;
}

type FieldSection = "in_conversation" | "post_conversation" | "pre-session";

interface FormFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  fieldSection: FieldSection;
  editingField?: FormField | null;
  onSubmit: (values: FormFieldValues, id?: string | null) => Promise<void>;
}

const FormFieldModal: React.FC<FormFieldModalProps> = ({
  isOpen,
  onClose,
  agent,
  fieldSection,
  editingField,
  onSubmit,
}) => {
  const isEditing = !!editingField;
  
  const getModalTitle = () => {
    const action = isEditing ? "Edit" : "Add";
    const fieldType = fieldSection === "post_conversation" ? "Extraction Field" : "Field";
    return `${action} ${fieldType}`;
  };

  const handleSubmit = async (values: FormFieldValues) => {
    try {
      await onSubmit(values, editingField?.id || null);
      onClose();
    } catch (error) {
      // Don't close modal on error, let the form handle error display
      console.error('Form submission error:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] my-8 max-h-[calc(100vh-4rem)] flex flex-col p-0">
        <div className="flex-shrink-0 p-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              {isEditing ? (
                <>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 text-blue-500"
                  >
                    <path
                      d="M11.8536 1.14645C11.6583 0.951184 11.3417 0.951184 11.1465 1.14645L3.71455 8.57836C3.62459 8.66832 3.55263 8.77461 3.50251 8.89155L2.04044 12.303C1.9599 12.491 2.00189 12.709 2.14646 12.8536C2.29103 12.9981 2.50905 13.0401 2.69697 12.9596L6.10847 11.4975C6.2254 11.4474 6.33168 11.3754 6.42164 11.2855L13.8536 3.85355C14.0488 3.65829 14.0488 3.34171 13.8536 3.14645L11.8536 1.14645ZM4.42161 9.28547L11.5 2.20711L12.7929 3.5L5.71455 10.5784L4.21924 11.2192L3.78081 10.7808L4.42161 9.28547Z"
                      fill="currentColor"
                      fillRule="evenodd"
                      clipRule="evenodd"
                    />
                  </svg>
                  {getModalTitle()}
                </>
              ) : (
                <>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 text-blue-500"
                  >
                    <path
                      d="M8 2.75C8 2.47386 7.77614 2.25 7.5 2.25C7.22386 2.25 7 2.47386 7 2.75V7H2.75C2.47386 7 2.25 7.22386 2.25 7.5C2.25 7.77614 2.47386 8 2.75 8H7V12.25C7 12.5261 7.22386 12.75 7.5 12.75C7.77614 12.75 8 12.5261 8 12.25V8H12.25C12.5261 8 12.75 7.77614 12.75 7.5C12.75 7.22386 12.5261 7 12.25 7H8V2.75Z"
                      fill="currentColor"
                      fillRule="evenodd"
                      clipRule="evenodd"
                    />
                  </svg>
                  {getModalTitle()}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
        </div>
        
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <FormFieldForm
            agent={agent}
            fieldSection={fieldSection}
            editingField={editingField}
            onSubmit={handleSubmit}
            onCancel={onClose}
            renderButtons={({ isLoading, isEditing, fieldSection }) => (
              <div className="sticky bottom-0 bg-background pt-4 mt-6 border-t">
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="border-gray-200 text-gray-700 hover:bg-gray-50"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className={
                      fieldSection === "post_conversation"
                        ? "bg-purple-600 hover:bg-purple-700 text-white"
                        : "bg-[#060520] hover:bg-[#0c0c37] text-white"
                    }
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {isEditing ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        {isEditing
                          ? fieldSection === "post_conversation"
                            ? "Update Extraction Field"
                            : "Update Field"
                          : fieldSection === "post_conversation"
                            ? "Add Extraction Field"
                            : "Add Field"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormFieldModal; 